import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { connect, useSelector } from "react-redux";
import withRouter from "components/Common/withRouter";
import { withTranslation } from "react-i18next";
import SidebarContent from "./SidebarContent";
import { Link } from "react-router-dom";

import logoLight from "../../assets/images/light-logo-coldblock.png";
import logoDark from "../../assets/images/dark-logo-coldblock.png";
import bvLogo from "../../assets/images/bureau-veritas/bureau_veritas_north_america_inc_logo-removebg-preview.png";

const clamp01 = n => Math.min(1, Math.max(0, n));
const srgbToLin = c => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
const relativeLuminance = ({ r, g, b }) => {
  const R = srgbToLin(r / 255);
  const G = srgbToLin(g / 255);
  const B = srgbToLin(b / 255);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

const parseCssColorToRGB = (str) => {
  if (!str) return null;

  let m = str.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (m) return { r: +m[1], g: +m[2], b: +m[3] };

  m = str.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (m) {
    let hex = m[1];
    if (hex.length === 3) hex = hex.split("").map(x => x + x).join("");
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }
  return null;
};

const extractBgRGB = (el) => {
  if (!el) return null;
  const cs = getComputedStyle(el);

  const bgc = cs.backgroundColor;
  if (bgc && !/rgba?\(\s*\d+,\s*\d+,\s*\d+,\s*0\s*\)/i.test(bgc)) {
    const rgb = parseCssColorToRGB(bgc);
    if (rgb) return rgb;
  }

  const bgi = cs.backgroundImage;
  if (bgi && bgi !== "none") {
    const rgbMatch = bgi.match(/rgba?\(\s*\d+,\s*\d+,\s*\d+/i);
    if (rgbMatch) {
      const rgbParsed = parseCssColorToRGB(rgbMatch[0].replace(/a?\(/i, "(") + ")");
      if (rgbParsed) return rgbParsed;
    }
    const hexMatch = bgi.match(/#[0-9a-f]{3,6}/i);
    if (hexMatch) {
      const hexParsed = parseCssColorToRGB(hexMatch[0]);
      if (hexParsed) return hexParsed;
    }
  }

  if (el.parentElement) return extractBgRGB(el.parentElement);
  return { r: 255, g: 255, b: 255 };
};

const pickLogoByBg = (el) => {
  const rgb = extractBgRGB(el);
  const L = relativeLuminance(rgb);

  const isDarkBg = L < 0.5;
  return { isDarkBg, rgb, L };
};

const Sidebar = (props) => {
  const menuRef = useRef(null);
  const [isDarkBg, setIsDarkBg] = useState(true);

  const { user: loggedUser } = useSelector(state => state.Login);
  const companyLogoSrc = loggedUser?.company?.logoUrl || bvLogo;
  const companyLogoAlt = loggedUser?.company?.name
    ? `${loggedUser.company.name} logo`
    : "Company logo";

  const recompute = () => {
    if (!menuRef.current) return;
    const { isDarkBg } = pickLogoByBg(menuRef.current);
    setIsDarkBg(isDarkBg);
  };

  useEffect(() => {
    const scheduleCompute = () => {
      requestAnimationFrame(() => requestAnimationFrame(recompute));
    };
    scheduleCompute();

    const onResize = () => scheduleCompute();
    window.addEventListener("resize", onResize);

    const targets = [
      () => menuRef.current,
      () => document.body,
      () => document.documentElement,
    ];

    const observers = targets.map(getEl => {
      const el = getEl();
      if (!el) return null;
      const mo = new MutationObserver(scheduleCompute);
      mo.observe(el, {
        attributes: true,
        attributeFilter: ["class", "style", "data-theme", "data-color-mode"],
        subtree: false,
      });
      return mo;
    });

    return () => {
      window.removeEventListener("resize", onResize);
      observers.forEach(mo => mo && mo.disconnect());
    };
  }, [props.router.location, props.layout]);

  const logoSrc = isDarkBg ? logoLight : logoDark;

  return (
    <React.Fragment>
      <div className="vertical-menu" ref={menuRef}>
        <div className="navbar-brand-box">
          <Link to="/" className="logo logo-dark">
            <span className="logo-sm">
              <img src={logoSrc} alt="ColdBlock logo" height="70" />
            </span>
            <span className="logo-lg">
              <img src={logoSrc} alt="ColdBlock logo" height="70" />
            </span>
          </Link>

          <Link to="/" className="logo logo-light mt-2 ms-2">
            <span className="logo-sm">
              <img src={logoSrc} alt="ColdBlock logo" height="70" />
            </span>
            <span className="logo-lg custom-logo-lg">
              <img src={logoSrc} alt="ColdBlock logo" height="70" />
            </span>
          </Link>
        </div>

        <div data-simplebar className="sidebar-scroll">
          <SidebarContent />
        </div>
        {/* <div className="sidebar-company-logo">
          <img src={companyLogoSrc} alt={companyLogoAlt} />
        </div> */}
        <div className="sidebar-background"></div>
      </div>
    </React.Fragment>
  );
};

Sidebar.propTypes = {
  type: PropTypes.string,
};

const mapStatetoProps = (state) => ({
  layout: state.Layout,
});

export default connect(
  mapStatetoProps,
  {}
)(withRouter(withTranslation()(Sidebar)));
